// src/models/chatModels/chatConversation.js

import { MessageStatus } from './chatMessage.jsx';

export class ChatConversation {
  constructor({
    id,
    participants,
    otherUserId,
    lastMessage,
    lastMessageSenderId,
    lastMessageTime,
    unreadCount = 0,
    lastMessageType = 'text',
    lastMessageStatus = MessageStatus.SENT,
    isArchived = false,
    isMuted = false,
    isPinned = false,
    mutedUntil = null,
    lastSeen = {},
    createdAt = null,
    deletedAt = null,
    deletedBy = [],
    otherUserName = null,
    otherUserAvatar = null,
  }) {
    this.id = id;
    this.participants = participants;
    this.otherUserId = otherUserId;
    this.lastMessage = lastMessage;
    this.lastMessageSenderId = lastMessageSenderId;
    this.lastMessageTime = lastMessageTime;
    this.unreadCount = unreadCount;
    this.lastMessageType = lastMessageType;
    this.lastMessageStatus = lastMessageStatus;
    this.isArchived = isArchived;
    this.isMuted = isMuted;
    this.isPinned = isPinned;
    this.mutedUntil = mutedUntil;
    this.lastSeen = lastSeen;
    this.createdAt = createdAt;
    this.deletedAt = deletedAt;
    this.deletedBy = deletedBy;
    this.otherUserName = otherUserName;
    this.otherUserAvatar = otherUserAvatar;
  }

  get conversationId() {
    return this.id;
  }

  // ── Factory: from a single ChatMessage ────────────────────────────────────

  static fromMessage(message, currentUserId) {
    const otherId = message.senderId === currentUserId
      ? message.receiverId
      : message.senderId;

    return new ChatConversation({
      id: message.conversationId,
      participants: [message.senderId, message.receiverId],
      otherUserId: otherId,
      lastMessage: message.content,
      lastMessageSenderId: message.senderId,
      lastMessageTime: message.timestamp,
      lastMessageStatus: message.status,
      unreadCount: message.receiverId === currentUserId ? 1 : 0,
    });
  }

  // ── Factory: from Firestore document ──────────────────────────────────────

  static fromFirestore(docId, data, currentUserId) {
    const participants = data.participants || [];
    const otherId = participants.find(p => p !== currentUserId) || '';

    let lastMsgTime = new Date();
    const rawLmt = data.lastMessageTime;
    if (rawLmt) {
      if (rawLmt.toDate) {
        lastMsgTime = rawLmt.toDate();
      } else if (rawLmt instanceof Date) {
        lastMsgTime = rawLmt;
      }
    }

    const unreadMap = data.unreadCount || {};
    const unread = unreadMap[currentUserId] || 0;

    const rawLastSeen = data.lastSeen || {};
    const lastSeen = {};
    for (const [uid, val] of Object.entries(rawLastSeen)) {
      if (val?.toDate) {
        lastSeen[uid] = val.toDate();
      } else if (val instanceof Date) {
        lastSeen[uid] = val;
      }
    }

    const toDateTime = (val) => {
      if (!val) return null;
      if (val.toDate) return val.toDate();
      if (val instanceof Date) return val;
      return null;
    };

    // Parse last message status from Firestore
    let lastMsgStatus = MessageStatus.SENT;
    const rawStatus = data.lastMessageStatus;
    if (rawStatus) {
      switch (rawStatus) {
        case 'delivered':
          lastMsgStatus = MessageStatus.DELIVERED;
          break;
        case 'read':
          lastMsgStatus = MessageStatus.READ;
          break;
        case 'sending':
          lastMsgStatus = MessageStatus.SENDING;
          break;
        case 'failed':
          lastMsgStatus = MessageStatus.FAILED;
          break;
        default:
          lastMsgStatus = MessageStatus.SENT;
      }
    }

    return new ChatConversation({
      id: docId,
      participants: participants,
      otherUserId: otherId,
      lastMessage: data.lastMessage || '',
      lastMessageSenderId: data.lastMessageSender || '',
      lastMessageTime: lastMsgTime,
      unreadCount: unread,
      lastMessageType: data.lastMessageType || 'text',
      lastMessageStatus: lastMsgStatus,
      isArchived: data.isArchived || false,
      isMuted: data.isMuted || false,
      isPinned: data.isPinned || false,
      mutedUntil: toDateTime(data.mutedUntil),
      lastSeen: lastSeen,
      createdAt: toDateTime(data.createdAt),
      deletedAt: toDateTime(data.deletedAt),
      deletedBy: data.deletedBy || [],
    });
  }

  // ── Update with a new message ─────────────────────────────────────────────

  updateWithMessage(message, currentUserId) {
    const isNewer = message.timestamp.getTime() > this.lastMessageTime.getTime();
    
    return new ChatConversation({
      id: this.id,
      participants: this.participants,
      otherUserId: this.otherUserId,
      lastMessage: isNewer ? message.content : this.lastMessage,
      lastMessageSenderId: isNewer ? message.senderId : this.lastMessageSenderId,
      lastMessageTime: isNewer ? message.timestamp : this.lastMessageTime,
      lastMessageStatus: isNewer ? message.status : this.lastMessageStatus,
      unreadCount: this.unreadCount + (message.receiverId === currentUserId ? 1 : 0),
      lastMessageType: isNewer ? message.messageType : this.lastMessageType,
      isArchived: this.isArchived,
      isMuted: this.isMuted,
      isPinned: this.isPinned,
      mutedUntil: this.mutedUntil,
      lastSeen: this.lastSeen,
      createdAt: this.createdAt,
      deletedAt: this.deletedAt,
      deletedBy: this.deletedBy,
      otherUserName: this.otherUserName,
      otherUserAvatar: this.otherUserAvatar,
    });
  }

  // ── copyWith ──────────────────────────────────────────────────────────────

  copyWith({
    id,
    participants,
    otherUserId,
    lastMessage,
    lastMessageSenderId,
    lastMessageTime,
    unreadCount,
    lastMessageType,
    lastMessageStatus,
    isArchived,
    isMuted,
    isPinned,
    mutedUntil,
    lastSeen,
    createdAt,
    deletedAt,
    deletedBy,
    otherUserName,
    otherUserAvatar,
  }) {
    return new ChatConversation({
      id: id ?? this.id,
      participants: participants ?? this.participants,
      otherUserId: otherUserId ?? this.otherUserId,
      lastMessage: lastMessage ?? this.lastMessage,
      lastMessageSenderId: lastMessageSenderId ?? this.lastMessageSenderId,
      lastMessageTime: lastMessageTime ?? this.lastMessageTime,
      unreadCount: unreadCount ?? this.unreadCount,
      lastMessageType: lastMessageType ?? this.lastMessageType,
      lastMessageStatus: lastMessageStatus ?? this.lastMessageStatus,
      isArchived: isArchived ?? this.isArchived,
      isMuted: isMuted ?? this.isMuted,
      isPinned: isPinned ?? this.isPinned,
      mutedUntil: mutedUntil ?? this.mutedUntil,
      lastSeen: lastSeen ?? this.lastSeen,
      createdAt: createdAt ?? this.createdAt,
      deletedAt: deletedAt ?? this.deletedAt,
      deletedBy: deletedBy ?? this.deletedBy,
      otherUserName: otherUserName ?? this.otherUserName,
      otherUserAvatar: otherUserAvatar ?? this.otherUserAvatar,
    });
  }

  // ── To JSON ──────────────────────────────────────────────────────────────

  toJSON() {
    return {
      id: this.id,
      participants: this.participants,
      otherUserId: this.otherUserId,
      lastMessage: this.lastMessage,
      lastMessageSenderId: this.lastMessageSenderId,
      lastMessageTime: this.lastMessageTime.toISOString(),
      unreadCount: this.unreadCount,
      lastMessageType: this.lastMessageType,
      lastMessageStatus: this.lastMessageStatus,
      isArchived: this.isArchived,
      isMuted: this.isMuted,
      isPinned: this.isPinned,
      mutedUntil: this.mutedUntil ? this.mutedUntil.toISOString() : null,
      lastSeen: Object.fromEntries(
        Object.entries(this.lastSeen).map(([k, v]) => [k, v.toISOString()])
      ),
      createdAt: this.createdAt ? this.createdAt.toISOString() : null,
      deletedAt: this.deletedAt ? this.deletedAt.toISOString() : null,
      deletedBy: this.deletedBy,
      otherUserName: this.otherUserName,
      otherUserAvatar: this.otherUserAvatar,
    };
  }

  toString() {
    return `ChatConversation(id: ${this.id}, other: ${this.otherUserId}, last: "${this.lastMessage}", unread: ${this.unreadCount}, status: ${this.lastMessageStatus})`;
  }
}

export default ChatConversation;